import { useState } from "react";
import { toast } from "sonner";
import { Download, DatabaseBackup } from "lucide-react";
import { api, errorText, SERVICES } from "@/lib/api";
import { nok, Card, Btn, PageHead, useData, Spinner, download, today } from "./ui";
import { OrderRow } from "./WorkOrders";
import { SalesReport } from "./SalesReport";
import { PendingSignatures } from "./PendingSignatures";

const Stat = ({ l, v, testid, tone }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{l}</div>
    <div className={`mt-1 font-display text-3xl font-black ${tone ?? ""}`}>{v}</div>
  </Card>
);

const Backups = () => {
  const [list, reload] = useData("/admin/backups");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Backup lagret"); reload(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="backups-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-xl font-bold uppercase">Backup</h3>
        <div className="flex flex-wrap gap-2">
          <Btn onClick={() => download("/admin/backup/download", `rbtun_backup_${today()}.json`)} data-testid="backup-download"><Download size={14} /> Last ned full backup</Btn>
          <Btn onClick={run} disabled={busy} data-testid="backup-run"><DatabaseBackup size={14} /> Lagre backup nå</Btn>
        </div>
      </div>
      <p className="mb-3 text-xs text-zinc-400">Automatisk backup av alle data lagres hver natt kl. 03:00. Bilder og dokumenter ligger i sikker fillagring og refereres i backupen.</p>
      <div className="space-y-1 text-sm" data-testid="backups-list">
        {(list ?? []).slice(0, 7).map((b) => (
          <button key={b.id} type="button" onClick={() => download(`/admin/backups/${b.id}/download`, `rbtun_backup_${b.created_at.slice(0, 10)}.json`)}
            className="flex w-full justify-between border-b border-white/5 py-1.5 text-left text-zinc-300 hover:text-white">
            <span>{new Date(b.created_at).toLocaleString("nb-NO")} · {b.trigger}</span><span className="font-mono text-xs">{Math.round(b.size / 1024)} kB</span>
          </button>
        ))}
        {list?.length === 0 && <p className="text-zinc-400">Ingen lagrede backuper ennå.</p>}
      </div>
    </Card>
  );
};

export const Dashboard = ({ go }) => {
  const [d] = useData("/admin/dashboard");
  if (!d || Array.isArray(d)) return <Spinner />;
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHead title="Oversikt" sub="Alle beløp i NOK" />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat l="Biler på lager" v={d.vehicles_in_stock} testid="stat-stock" />
        <Stat l="Biler solgt" v={d.vehicles_sold} testid="stat-sold" />
        <Stat l="Lagerverdi (kost)" v={nok(d.stock_cost)} testid="stat-stock-cost" />
        <Stat l="Herav innkjøp" v={nok(d.stock_purchase)} testid="stat-stock-purchase" />
        <Stat l="Salgsinntekt (netto)" v={nok(d.sales_revenue)} testid="stat-sales" />
        <Stat l="Fortjeneste solgte biler" v={nok(d.sales_profit)} testid="stat-profit" tone={d.sales_profit < 0 ? "text-red-400" : "text-emerald-400"} />
        <Stat l="Verkstedinntekt inkl. MVA" v={nok(d.workshop_revenue)} testid="stat-workshop" />
        <Stat l="Utestående betalinger" v={nok(d.outstanding)} testid="stat-outstanding" tone={d.outstanding > 0 ? "text-amber-400" : ""} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-xl font-bold uppercase">Åpne jobber ({d.open_jobs})</h3>
            <Btn onClick={() => go("orders")} data-testid="dash-go-orders">Alle</Btn></div>
          <div className="space-y-2">{d.open_jobs_list.length === 0 ? <p className="text-sm text-zinc-400">Ingen åpne jobber.</p> : d.open_jobs_list.map((o) => <OrderRow key={o.id} o={o} onClick={() => go("orders", o.id)} />)}</div>
        </Card>
        <Card>
          <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-xl font-bold uppercase">Nye forespørsler ({d.new_inquiries})</h3>
            <Btn onClick={() => go("inquiries")} data-testid="dash-go-inquiries">Alle</Btn></div>
          <div className="space-y-2" data-testid="dash-inquiries">
            {d.recent_inquiries.map((i) => (
              <div key={i.id} className="border border-white/10 p-3 text-sm">
                <div className="flex justify-between gap-2"><span className="font-bold">{i.name}</span><span className="font-mono text-xs text-zinc-400">{new Date(i.created_at).toLocaleDateString("nb-NO")}</span></div>
                <div className="text-xs text-zinc-400">{SERVICES.find((s) => s.value === i.service)?.label ?? i.service} · {i.car} {i.regnr ?? ""}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <PendingSignatures />
      <SalesReport />
      <Backups />
    </div>
  );
};
